import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { from, Observable, switchMap } from 'rxjs';

import { MediaDetails, StandardMedia } from '@api/index';
import { TriggerMediaModal } from '@shared/trigger-media-model/trigger-media-model.component';
import { ViewMediaModal } from '@shared/view-media-modal/view-media.modal';
import { MediaCreateModal } from '@app/admin/media/media-create/media-create-modal/media-create.modal';
import { MediaSelectModal } from '@admin/media/media-select.modal';
import { Media } from './media.service';

@Injectable({
  providedIn: 'root',
})
export class MediaModalService {
  constructor(private dialog: MatDialog) { }

  openCreateMediaModal(data?: any): Observable<Media> {
    const dialogRef = this.dialog.open(MediaCreateModal, {
      width: '410px', 
      maxHeight: '90vh',
      data: {
        ...data,
      },
    });

    return dialogRef.afterClosed()
  }

  // Importing the MediaSelectModal component dynamically on click as a workaraund for lazy loading
  // We need to refacor media.component and use separate tabs for the grid and list view and also add a separate component for media select
  openSelectMediaModal(data?: any): Observable<Media> {
    return from(import('../../admin/media/media.module')).pipe(
      switchMap(() => {
        const dialogRef = this.dialog.open(MediaSelectModal, {
          width: '54%',
          height: '85%',
          data: { ...data },
        });
        return dialogRef.afterClosed();
      })
    );
  }

  setModal(media: Media, form: any, propertyName: string, list: any[] , isDefault: boolean){
    if (media) {
      const mediaObject = {
        mediaId: media.id,
        name: media.name,
        type: media.type,
        duration: media.duration,
        mediaFileStreamingLink: media.mediaFileStreamingLink,
        mediaFileThumbnailStreamingLink: media.mediaFileThumbnailStreamingLink,
        filename: media.filename,      
        ...(media.type === 2 && {
          [isDefault ? 'defaultMediaPresentationTime' : 'presentationTime']: media.duration
        }),
      };

      const timeField = propertyName === 'defaultMediaDetails' ? 'defaultMediaPresentationTime' : 'presentationTime';

      const control = form.get(propertyName)?.get(timeField) || form.get(timeField);

      if (!control) {
        return;
      }

      if (media.type === 2) {
        control.setValue(media.duration);
        control.disable();
      } else {
        control.enable();
      }

      const findMedia = list.find((sm) => sm?.mediaId === media.id);
      form.markAsDirty();
      form.get(propertyName).patchValue(findMedia || mediaObject);
      
      if (!findMedia) {
        list.unshift(mediaObject);
      }
      
    } else {
      return;
    }
  }
  
  openViewMediaModal(standardMedia: StandardMedia | MediaDetails): void {
    const title = standardMedia?.filename ? standardMedia.name + ' - ' + standardMedia.filename : standardMedia.name; 
    
    this.dialog.open(ViewMediaModal, {
      width: '80vw',
      maxHeight: '80vh',
      panelClass: 'h-full',
      data: {
        title,
        media: {
          mediaFileStreamingLink: standardMedia.mediaFileStreamingLink,
          mediaFileThumbnailStreamingLink: standardMedia.mediaFileThumbnailStreamingLink,
          type: standardMedia?.type
        }
      }
    });
  }
  openTriggerMediaModal(triggers?: any, updateTriggersCallback?: (triggers: any) => void) {
    const dialogRef = this.dialog.open(TriggerMediaModal, {
      autoFocus: false,
      data: {
        triggers
      },
    });

    dialogRef.afterClosed().subscribe((triggerForm: any) => {
      if (triggerForm) {
        triggers = {
          weatherConditions: (triggerForm?.selectedIcons && triggerForm?.selectedIcons.length > 0) ? triggerForm?.selectedIcons : null,
          temperature: (triggerForm?.temperature && Object.keys(triggerForm?.temperature).length > 0) ? triggerForm?.temperature : null
        };
        updateTriggersCallback(triggers);
      }
    });
  }
}
